import Model, { attr, belongsTo, hasMany } from "@ember-data/model";
import { tracked } from "@glimmer/tracking";
import type ExternalFluxLoincMapping from "parog-web/models/external-flux-loinc-mapping";
import type BioExamOrder from "./bio-exam-order";

type ValueType = "number" | "string";
export type Specimen = "BLD" | "UR";

export default class Loinc extends Model {
  @tracked
  equivalentLoincUnit?: string;
  // Content coming from JDVCircuitBio

  /**
   * This is also the primary key / id of the record.
   * Thus it can be used to get a record from the LOINC code:
   *
   * ```js
   * this.store.peekRecord("loinc", "12345-5");
   * ```
   */
  @attr("string") declare loinc: string;
  @attr("string") declare label: string;
  @attr("string") declare recommendedUnit: string | null;
  @attr("string") declare specimen: Specimen | null;
  @attr("string") declare loincChapterLabel: string;
  @attr("number") declare rank: number;

  // Other content coming from Sêmeia
  @attr("string") declare customLabel: string | null;
  @attr("number") declare minReferenceValue: number | null;
  @attr("number") declare maxReferenceValue: number | null;
  @attr("string") declare placeholder: string | null;
  @attr("string") declare valueType: ValueType | null;
  @attr("string") declare defaultUnit: string | null;
  @attr("string") declare referenceValuesMessage: string | null;
  @attr("boolean") declare zeroable: boolean;
  @attr() declare availableUnits: string[];
  @attr("number") declare identicalValuesEpsilonRatio: number;

  // There are two source of units:
  // -  `recommended_unit`, which comes from JDVCircuitBio,
  // -  `default_unit`, which is from us and let us override or define another unit.
  //
  // `default_unit` has the priority over `recommended_unit`.
  get unit(): string | null {
    return this.defaultUnit || this.recommendedUnit;
  }

  get displayLabel(): string {
    return this.customLabel || this.label;
  }

  @hasMany("external-flux-loinc-mapping", {
    async: false,
    inverse: "bioExamObservation"
  })
  declare externalFluxLoincMappings: ExternalFluxLoincMapping[];

  @belongsTo("bio-exam-order", {
    async: false,
    inverse: "bioExamObservations"
  })
  declare bioExamOrder: BioExamOrder;

  get supportedUnits() {
    if (!this.unit) {
      return [];
    }

    const supportedUnits = [
      ...this.availableUnits,
      this.equivalentLoincUnit
    ].filter(Boolean);

    if (supportedUnits.includes(this.unit)) {
      return supportedUnits;
    }

    return [...supportedUnits, this.unit];
  }

  get hasAvailableUnits() {
    return Boolean(this.availableUnits.length || this.equivalentLoincUnit);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    loinc: Loinc;
  }
}
